// Component: VideoTemplateModal
// Purpose: Preview modal for a video template. Loads a signed URL for playback
// and shows tags and optional Canva edit link. Closes on backdrop or button.
import React, { useEffect, useState } from 'react';
import { X, Play, ExternalLink } from 'lucide-react';
import { getVideoTemplateSignedUrl } from '../../../api/content/videoTemplatesApi';
import { LoadingSpinner } from '../../ui/feedback/loading-spinner.jsx';

export function VideoTemplateModal({ isOpen, onClose, videoTemplate }) {
  const [signedUrl, setSignedUrl] = useState('');
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);
  const [urlError, setUrlError] = useState('');

  useEffect(() => {
    if (isOpen && videoTemplate?.id) {
      fetchSignedUrl();
    } else {
      // Reset state when modal closes
      setSignedUrl('');
      setUrlError('');
    }
  }, [isOpen, videoTemplate?.id]);

  const fetchSignedUrl = async () => {
    setIsLoadingUrl(true);
    setUrlError('');
    
    try {
      const response = await getVideoTemplateSignedUrl(videoTemplate.id);
      if (response && response.signedUrl) {
        setSignedUrl(response.signedUrl);
      } else {
        throw new Error('Failed to get video URL');
      }
    } catch (error) {
      console.error('Error fetching video template signed URL:', error);
      setUrlError('Failed to load video. Please try again.');
    } finally {
      setIsLoadingUrl(false);
    }
  };

  if (!isOpen || !videoTemplate) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleRetry = () => {
    fetchSignedUrl();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={handleBackdropClick}>
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />

        {/* Modal panel */}
        <div className="inline-block w-full max-w-4xl px-6 py-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-900 shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-start justify-between pb-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">
                {videoTemplate.title || 'Video Template Preview'}
              </h3>
              <div className="mt-1 flex flex-wrap gap-2">
                {videoTemplate.tags && videoTemplate.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              {videoTemplate.canvaLink && (
                <div className="mt-2">
                  <a
                    href={videoTemplate.canvaLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Edit in Canva
                  </a>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="ml-4 bg-white dark:bg-gray-900 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Video content */}
          <div className="mt-6">
            {isLoadingUrl ? (
              <div className="flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg" style={{ aspectRatio: '16/9', minHeight: '300px' }}>
                <div className="text-center">
                  <LoadingSpinner message="Loading video..." />
                </div>
              </div>
            ) : urlError ? (
              <div className="flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg" style={{ aspectRatio: '16/9', minHeight: '300px' }}>
                <div className="text-center">
                  <div className="text-red-500 mb-4">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">{urlError}</p>
                  <button
                    onClick={handleRetry}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : signedUrl ? (
              <div className="bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
                <video
                  src={signedUrl}
                  controls
                  className="w-full h-full object-contain"
                  preload="metadata"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            ) : (
              <div className="flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg" style={{ aspectRatio: '16/9', minHeight: '300px' }}>
                <div className="text-center">
                  <Play className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">Video not available</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 